
import React from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Bold, Italic, List, Link2 } from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "輸入內容...",
  rows = 10
}) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const insertFormatting = (prefix: string, suffix: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    
    const beforeText = value.substring(0, start);
    const afterText = value.substring(end);
    
    const newText = beforeText + prefix + selectedText + suffix + afterText;
    onChange(newText);
    
    // 重新設置光標位置
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + prefix.length,
        start + prefix.length + selectedText.length
      );
    }, 0);
  };

  const convertToHtml = (text: string): string => {
    return text
      // 處理標題
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      // 處理粗體
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // 處理斜體
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // 處理無序列表
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      // 包裝連續的 li 標籤
      .replace(/(<li>.*<\/li>\s*)+/gs, (match) => `<ul>${match}</ul>`)
      // 處理有序列表
      .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
      // 處理換行
      .replace(/\n\n/g, '</p><p>')
      .replace(/^(?!<[hul])/gm, '<p>')
      .replace(/(?!<\/[hul]>)$/gm, '</p>')
      // 清理多餘的 p 標籤
      .replace(/<p><\/p>/g, '')
      .replace(/<p>(<[hul])/g, '$1')
      .replace(/(<\/[hul]>)<\/p>/g, '$1');
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2 border-b pb-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => insertFormatting('## ', '')}
          title="標題"
        >
          H2
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => insertFormatting('### ', '')}
          title="子標題"
        >
          H3
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => insertFormatting('**', '**')}
          title="粗體"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => insertFormatting('*', '*')}
          title="斜體"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => insertFormatting('- ', '')}
          title="列表"
        >
          <List className="h-4 w-4" />
        </Button>
      </div>
      
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="min-h-[200px] font-mono text-sm"
      />
      
      <div className="text-xs text-gray-500">
        <p className="mb-1">格式化說明：</p>
        <ul className="space-y-1">
          <li>## 標題 - 主標題</li>
          <li>### 子標題 - 次標題</li>
          <li>**粗體文字** - 粗體</li>
          <li>*斜體文字* - 斜體</li>
          <li>- 列表項目 - 無序列表</li>
        </ul>
      </div>
      
      {/* 隱藏的轉換後HTML，用於表單提交 */}
      <input
        type="hidden"
        value={convertToHtml(value)}
        onChange={() => {}}
      />
    </div>
  );
};

export default RichTextEditor;
